// OpenHomeConfigurePost.tsx
import React, { useEffect, useRef, useState } from "react";
import "./OpenHomeConfigurePost.scss";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TextField from "@mui/material/TextField";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import LinkIcon from "../../../../../components/icons/LinkIcon";
import LeftArrowIcon from "../../../../../components/icons/LeftArrowIcon";
import RightArrowIcon from "../../../../../components/icons/RightArrowIcon";
import RoundTrueIcon from "../../../../../components/icons/RoundTrueIcon";
import { PropertyApi } from "../../../../../utils/api/Property";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { CommonEnums } from "../../../../../enums/common.enums";
import CustomPageLoader from "../../../../../components/loaders/CustomPageLoader";
import { CommonHelpers } from "../../../../../utils/helpers/common.helpers";
import PreviewModal from "../../propertypost/view/PreviewModal";
import { Modal } from "@mui/material";
import { getImageUrl } from "../../../../../utils/helpers/common";
import ConfigureMenuIcon from "./ConfigureMenuIcon";

interface PropertyItem {
  _id?: string;
  id?: string;
  address?: string;
  property_no?: string;
  property_no_and_street_name?: string;
  city?: string;
  state?: string;
  post_code?: string;
  thumbnail_image_ref?: { compressed_file_path?: string };
  agent_ref?: {
    profile_image_ref?: { compressed_file_path?: string };
    principal_agent_first_name?: string;
    principal_agent_last_name?: string;
    principal_phone_country_code?: string;
    principal_agent_mobile_number?: string;
  };
  property_type_ref?: { name?: string };
  listing_type?: { name?: string };
  property_stage_ref?: { name?: string };
  price_type?: { slug?: string };
  price?: string;
  start_price?: string;
  end_price?: string;
  eoi_datetime?: string;
  createdAt?: string;
}

interface OpenHomeConfigurePostProps {
  postType?: string | null;
  postStatus?: string | null;
  scheduleDatetime?: string | null;
  openHomeDate?: dayjs.Dayjs | null;
  properties?: any[];
  onUpdatePostConfiguration?: (data: {
    postType: string;
    scheduleDatetime: string;
  }) => Promise<void>;
}

const OpenHomeConfigurePost = ({
  postType = null,
  postStatus = null,
  scheduleDatetime = null,
  openHomeDate = null,
  properties = [],
  onUpdatePostConfiguration,
}: OpenHomeConfigurePostProps) => {
  const [activeTab, setActiveTab] = useState("Facebook");
  const [sameTextToggle, setSameTextToggle] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [postPreviewIndex, setPostPreviewIndex] = useState(0);
  const [date, setDate] = useState<dayjs.Dayjs | null>(null);
  const [timeTo, setTimeTo] = useState<dayjs.Dayjs | null>(null);

  const [loading, setLoading] = useState(true);
  const [selectedPostType, setSelectedPostType] = useState<string | null>(
    postType
  );
  const scheduleFormRef = useRef<HTMLDivElement | null>(null);
  const [confirmedPostType, setConfirmedPostType] = useState<string | null>(
    null
  );
  const [loadingPostType, setLoadingPostType] = useState<string | null>(null);
  const [step, setStep] = useState("select");
  const [isSaving, setIsSaving] = useState(false);
  const [openHomePost, setOpenHomePost] = useState<any | null>(null);
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const [isUpdateTemplateOpen, setIsUpdateTemplateOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [initialApplied, setInitialApplied] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const [processingModalOpen, setProcessingModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState<string[]>([]);

  const [modalInitialIndex, setModalInitialIndex] = useState(0);

  useEffect(() => {
    if (initialApplied) return;

    const url = new URL(window.location.href);
    const paramType = url.searchParams.get("post_type");

    if (paramType) {
      setSelectedPostType(paramType);
      setConfirmedPostType(paramType);
      setStep("configure");
      setInitialApplied(true);
    } else if (postType) {
      setSelectedPostType(postType);
      setConfirmedPostType(postType);
      setStep("configure");
      setInitialApplied(true);
    }
  }, [postType, initialApplied]);

  useEffect(() => {
    if (scheduleDatetime) {
      const parsed = dayjs(scheduleDatetime);
      if (parsed.isValid()) {
        setDate(parsed);
        setTimeTo(parsed);
      }
    }
  }, [scheduleDatetime]);

  useEffect(() => {
    setTimeout(() => setLoading(false), 3000);
  }, []);

  const firstProperty =
    properties && properties.length > 0 ? properties[0] : null;

  const postTypes = [
    { label: "Image Post", value: "image_post" },
    { label: "Video Post", value: "video_post" },
    { label: "Story Post", value: "story_post" },
    { label: "Reel Post", value: "reel_post" },
  ];

  const tabs = [
    "Facebook",
    "Instagram",
    "Google Business",
    "Linkedin",
    "Youtube",
  ];

  useEffect(() => {
    if (!scheduleId) return;

    let interval: NodeJS.Timeout | null = null;

    const fetchPost = async (isInitial = false) => {
      try {
        if (isInitial) setLoading(true);

        const res = await PropertyApi.getAgencyOpenHomeScheduleById(scheduleId);
        if (res?.data?.success) {
          const updatedPost = res.data.data;
          setOpenHomePost(updatedPost);

          if (updatedPost.post_type) {
            if (!selectedPostType) {
              setSelectedPostType(updatedPost.post_type);
              setConfirmedPostType(updatedPost.post_type);
              setStep("configure");
            }

            const url = new URL(window.location.href);
            url.searchParams.set("post_type", updatedPost.post_type);
            window.history.replaceState({}, "", url.toString());
          }

          const updatedStatuses = [
            updatedPost?.image_post_design_activity_ref?.design_status,
            updatedPost?.story_post_design_activity_ref?.design_status,
            updatedPost?.video_post_design_activity_ref?.design_status,
            updatedPost?.reel_post_design_activity_ref?.design_status,
          ];

          const shouldPoll =
            updatedStatuses.includes("processing") ||
            !updatedStatuses.includes("pending_for_social_media_upload");

          if (shouldPoll && !interval) {
            interval = setInterval(() => fetchPost(false), 3000);
          } else if (!shouldPoll && interval) {
            clearInterval(interval);
            interval = null;
          }
        }
      } catch (err) {
        console.error("Failed to fetch property post:", err);
        if (isInitial) toast.error("Unable to load property post");
      } finally {
        if (isInitial) setLoading(false);
      }
    };

    fetchPost(true);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [scheduleId]);

  const images = [
    "/images/property-inside.jpg",
    "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg",
    "https://as2.ftcdn.net/jpg/02/61/89/29/1000_F_261892957_6jyBXvEgM79iYr1eEiJKCosnVPJdvHHr.jpg",
  ];

  const handlePostPreviewPrev = () => {
    setPostPreviewIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handlePostPreviewNext = () => {
    setPostPreviewIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const fullText = `Featuring: 5 🛌  Bedrooms | 5 🛀  Bathrooms | 2 🚗 Car Spaces... more
💸 Price Range: $1,675,000 - $1,842,000
Discover a lifestyle that's more than extraordinary – it's truly exceptional. Nestled in the heart of Berwick Waters, this stunning property at 6 Rockbank Road offers everything you need to live like a star.
Don't miss the chance to experience the allure of celebrity living. Come and see what makes 6 Rockbank Road the ultimate address for those who demand the best.
Your dream lifestyle awaits!
📸 Explore More: https://www.realestate.com.au/property-house-vic-clyde...
For more information or to book a viewing,

Lern more at BrandFeeds [Brand feeds Link for this property here]

Contact
Sukhdeep Singh 📲 0420 879 430
Mohan Singh 📲 0426 250 579
#celebrityliving #dreamhome #luxurylifestyle #clydenorthrealestate 
#berwickwaters #melbournerealestate #homesforsale #luxuryhomes 
#realestategoals #propertyforsale #dreamliving #familyhome 
#investmentproperty`;

  const shortText = `Experience Celebrity Living at 📍 6 Rockbank Road, Clyde North Spaces...`;

  const handlePostTypeClick = (typeValue: string) => {
    setSelectedPostType(typeValue);

    const url = new URL(window.location.href);
    url.searchParams.set("post_type", typeValue);
    window.history.replaceState({}, "", url.toString());

    const activity = designActivityMap[typeValue];
    if (activity && activity._id) {
      setStep("configure");
    } else {
      setStep("confirm");
    }
  };

  const handleConfirm = () => {
    if (selectedPostType) {
      setConfirmedPostType(selectedPostType);
      setLoadingPostType(selectedPostType);
      setTimeout(() => {
        setLoadingPostType(null);
        setStep("configure");
      }, 1500);
    }
  };

  const handleShowScheduleForm = () => {
    setShowScheduleForm(true);

    setTimeout(() => {
      scheduleFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  // for pending do it later

  //   useEffect(() => {
  //   if (openHomePost?.schedule_datetime) {
  //     setScheduleDateTime(dayjs(openHomePost.schedule_datetime));
  //     setShowScheduleForm(true);
  //   }
  // }, [openHomePost]);

  const handleUpdatePostType = async () => {
    if (!selectedPostType) return;

    const openHomeScheduleId = scheduleId;
    if (!openHomeScheduleId) {
      toast.error("Missing schedule ID");
      return;
    }

    try {
      setProcessingModalOpen(true);
      setIsProcessing(true);
      setLoadingPostType(selectedPostType);

      const payload = {
        post_type: selectedPostType,
        finalize: true,
      };

      const res = await PropertyApi.updateOpenHomePost(
        openHomeScheduleId,
        payload
      );

      if (res?.data?.success) {
        toast.success("Post type generated successfully!");

        setConfirmedPostType(selectedPostType);
        setStep("configure");

        startPolling();
      } else {
        toast.error(res?.data?.message || "Failed to generate post type");
      }
    } catch (err) {
      console.error("Confirm failed:", err);
      toast.error("Something went wrong while confirming post type");
    } finally {
      setLoadingPostType(null);
      setIsProcessing(false);
      setProcessingModalOpen(false);
    }
  };

  const handlePreview = (clickedImageIndex: number) => {
    setModalImages(previewImages);
    setModalInitialIndex(clickedImageIndex);
    setIsPreviewOpen(true);
  };

  const handleScheduleConfirm = async () => {
    if (!date || !timeTo) {
      console.error("Date and time are required");
      return;
    }

    if (!scheduleId) {
      toast.error("Schedule ID is missing");
      return;
    }

    const formattedDatetime = date
      .hour(timeTo.hour())
      .minute(timeTo.minute())
      .second(0)
      .millisecond(0)
      .toISOString();

    setProcessingModalOpen(true);
    setIsProcessing(true);

    try {
      const res = await PropertyApi.updateOpenHomePost(scheduleId, {
        schedule_datetime: formattedDatetime,
      });

      if (res?.data?.success) {
        toast.success("Schedule updated successfully!");
        setShowScheduleForm(false);
      } else {
        toast.error(res?.data?.message || "Failed to update schedule");
      }
    } catch (error) {
      console.error("Error saving schedule:", error);
      toast.error("Something went wrong while saving schedule");
    } finally {
      setIsProcessing(false);
      setProcessingModalOpen(false);
    }
  };

  const handlePostNow = async () => {
    setIsSaving(true);

    try {
      const currentDatetime = dayjs().toISOString();

      if (onUpdatePostConfiguration && selectedPostType) {
        await onUpdatePostConfiguration({
          postType: selectedPostType,
          scheduleDatetime: currentDatetime,
        });
      }
    } catch (error) {
      console.error("Error posting now:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const renderDesignImage = (item: any) => {
    const designActivityRef = item?._id ? item : null;

    const assetUrlRaw = designActivityRef?.design_task_id?.output_image_path;
    const designTaskStatus = designActivityRef?.design_task_id?.task_status;
    const postType =
      designActivityRef?.design_post_type || item?.design_post_type;

    const isLoading = designTaskStatus === CommonEnums.taskStatus.queued;
    const isErrored = designTaskStatus === CommonEnums.taskStatus.errored;

    const assetUrl = assetUrlRaw
      ? CommonHelpers.getImageUrlFromImageTool({ path: assetUrlRaw })
      : null;

    console.log("asset url", assetUrl);
    console.log("DesignActivityRef", designActivityRef);
    console.log("Design Task ID", designActivityRef?.design_task_id);
    console.log(
      "Output Image Path",
      designActivityRef?.design_task_id?.output_image_path
    );

    if (isErrored) {
      return (
        <div className="activity-image-section image-placeholder-container">
          <img
            src="/images/image-video-generation-error-fallback-image-for-card.png"
            alt="Error Preview"
            className="activity-thumbnail placeholder-image"
          />
        </div>
      );
    }

    if (isLoading) {
      const fallback =
        postType === CommonEnums.postType.video_post.key
          ? "/images/square-fallback-image-for-video-preview.png"
          : "/images/square-fallback-image-for-image-preview.png";

      return (
        <div className="activity-image-section image-placeholder-container">
          <img
            src={fallback}
            alt="Loading Preview"
            className="activity-thumbnail placeholder-image"
          />
          <div className="loader-container">
            <CustomPageLoader className="activity-card-loader" />
            <p className="processing-text font-poppins-400">Processing...</p>
          </div>
        </div>
      );
    }

    if (postType === CommonEnums.postType.story_post.key) {
      return (
        <div className="activity-image-section story-post-image-container">
          <img
            src={
              assetUrl ||
              "/images/image-video-generation-error-fallback-image-for-card.png"
            }
            alt="Story Preview"
            className="activity-thumbnail"
            onError={(e: any) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "/images/image-video-generation-error-fallback-image-for-card.png";
            }}
          />
        </div>
      );
    }

    if (postType === CommonEnums.postType.video_post.key) {
      return (
        <div className="activity-image-section video-placeholder-collection">
          <img
            src={
              assetUrl ||
              "/images/image-video-generation-error-fallback-image-for-card.png"
            }
            alt="Video Preview"
            className="activity-thumbnail"
            onError={(e: any) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "/images/image-video-generation-error-fallback-image-for-card.png";
            }}
          />
        </div>
      );
    }

    if (postType === CommonEnums.postType.reel_post.key) {
      return (
        <div className="activity-image-section reel-placeholder-collection">
          <img
            src={
              assetUrl ||
              "/images/image-video-generation-error-fallback-image-for-card.png"
            }
            alt="Reel Preview"
            className="activity-thumbnail"
            onError={(e: any) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "/images/image-video-generation-error-fallback-image-for-card.png";
            }}
          />
        </div>
      );
    }

    return (
      <div className="activity-image-section">
        <img
          src={
            assetUrl ||
            "/images/image-video-generation-error-fallback-image-for-card.png"
          }
          alt="Image Preview"
          className="activity-thumbnail"
          onError={(e: any) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src =
              "/images/image-video-generation-error-fallback-image-for-card.png";
          }}
        />
      </div>
    );
  };

  const designStatuses = [
    openHomePost?.image_post_design_activity_ref?.design_status,
    openHomePost?.story_post_design_activity_ref?.design_status,
    openHomePost?.video_post_design_activity_ref?.design_status,
    openHomePost?.reel_post_design_activity_ref?.design_status,
  ].filter(Boolean);

  const hasPendingForUpload = designStatuses.includes(
    "pending_for_social_media_upload"
  );

  function resolveOpenHomeTemplateType(postType: string, listingType: string) {
    if (
      postType === CommonEnums.postTypes.image_post &&
      listingType === CommonEnums.listing_Type.rent
    ) {
      return CommonEnums.templateTypes.open_home_for_rent_listing_image_post
        .slug;
    }
    if (
      postType === CommonEnums.postTypes.story_post &&
      listingType === CommonEnums.listing_Type.rent
    ) {
      return CommonEnums.templateTypes.open_home_for_rent_listing_story_post
        .slug;
    }
    if (
      postType === CommonEnums.postTypes.story_post &&
      listingType === CommonEnums.listing_Type.sale
    ) {
      return CommonEnums.templateTypes.open_home_for_sale_story_post.slug;
    }
    if (
      postType === CommonEnums.postTypes.video_post &&
      listingType === CommonEnums.listing_Type.rent
    ) {
      return CommonEnums.templateTypes.open_home_for_rent_video_post.slug;
    }
    if (
      postType === CommonEnums.postTypes.video_post &&
      listingType === CommonEnums.listing_Type.sale
    ) {
      return CommonEnums.templateTypes.open_home_for_sale_video_post.slug;
    }
    if (
      postType === CommonEnums.postTypes.reel_post &&
      listingType === CommonEnums.listing_Type.rent
    ) {
      return CommonEnums.templateTypes.open_home_for_rent_reel_post.slug;
    }
    if (
      postType === CommonEnums.postTypes.reel_post &&
      listingType === CommonEnums.listing_Type.sale
    ) {
      return CommonEnums.templateTypes.open_home_for_sale_reel_post.slug;
    }

    // default fallback:
    return CommonEnums.templateTypes.open_home_for_sale_image_post.slug;
  }

  const getSelectedTemplateId = () => {
    switch (selectedPostType) {
      case "image_post":
        return (
          openHomePost?.image_post_design_activity_ref?.design_template_id ||
          openHomePost?.template_id ||
          ""
        );
      case "video_post":
        return (
          openHomePost?.video_post_design_activity_ref?.design_template_id ||
          openHomePost?.template_id ||
          ""
        );
      case "reel_post":
        return (
          openHomePost?.reel_post_design_activity_ref?.design_template_id ||
          openHomePost?.template_id ||
          ""
        );
      case "story_post":
        return (
          openHomePost?.story_post_design_activity_ref?.design_template_id ||
          openHomePost?.template_id ||
          ""
        );
      default:
        return openHomePost?.template_id || "";
    }
  };

  const designActivities = [
    openHomePost?.image_post_design_activity_ref,
    openHomePost?.story_post_design_activity_ref,
    openHomePost?.video_post_design_activity_ref,
    openHomePost?.reel_post_design_activity_ref,
  ]
    .filter((ref) => ref && ref._id)
    .flatMap((ref) => {
      if (ref?.design_task_id?.child_design_tasks?.length) {
        return [
          ref,
          ...ref.design_task_id.child_design_tasks.map((child: any) => ({
            ...ref,
            design_task_id: {
              ...ref.design_task_id,
              output_image_path: child.output_image_path,
              task_status: child.task_status ?? ref.design_task_id.task_status,
            },
          })),
        ];
      }
      return [ref];
    });

  const designActivityMap: Record<string, any> = {
    image_post: openHomePost?.image_post_design_activity_ref,
    story_post: openHomePost?.story_post_design_activity_ref,
    video_post: openHomePost?.video_post_design_activity_ref,
    reel_post: openHomePost?.reel_post_design_activity_ref,
  };

  const currentDesignActivity = selectedPostType
    ? designActivityMap[selectedPostType]
    : null;

  const filteredDesignActivities = designActivities.filter(
    (ref) => ref?.design_post_type === selectedPostType
  );

  const previewImages: string[] = filteredDesignActivities
    .map((ref: any) => {
      if (ref?.design_task_id?.output_image_path) {
        return CommonHelpers.getImageUrlFromImageTool({
          path: ref.design_task_id.output_image_path,
        });
      }
      return null;
    })
    .filter((url): url is string => Boolean(url));

  const startPolling = () => {
    if (!scheduleId || pollingRef.current) return;

    pollingRef.current = setInterval(async () => {
      try {
        const res = await PropertyApi.getOpenHomeScheduleById(scheduleId);
        if (res?.data?.success) {
          const updatedPost = res.data.data;
          setOpenHomePost(updatedPost);

          const designActivities = [
            updatedPost?.image_post_design_activity_ref,
            updatedPost?.story_post_design_activity_ref,
            updatedPost?.video_post_design_activity_ref,
            updatedPost?.reel_post_design_activity_ref,
          ].filter((a) => a && a._id);

          const allReady = designActivities.every(
            (a) => a.design_status === "pending_for_social_media_upload"
          );

          if (allReady && pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        }
      } catch (err) {
        console.error("Polling error:", err);
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const isDesignReady = (activity?: any) => {
    if (!activity) return false;
    return (
      activity.design_status === "pending_for_social_media_upload" &&
      !!activity.design_task_id?.output_image_path
    );
  };

  function formatPostStatus(status?: string): string {
    if (!status) return "-";
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  function getPostStatusDisplay(item: any): string {
    if (!item) return "-";

    if (item.post_status === "cancelled") {
      return "Cancelled";
    }

    const designActivityRef =
      item.image_post_design_activity_ref ||
      item.story_post_design_activity_ref ||
      item.video_post_design_activity_ref ||
      item.reel_post_design_activity_ref;

    const scheduleDatetime = item.schedule_datetime
      ? item.schedule_datetime.$date
        ? new Date(item.schedule_datetime.$date)
        : new Date(item.schedule_datetime)
      : null;

    if (scheduleDatetime) {
      return "Scheduled to Post";
    }

    if (!designActivityRef) {
      return formatPostStatus(item.post_status || "-");
    }

    const designStatus = designActivityRef.design_status;

    if (designStatus === "pending_for_social_media_upload") {
      return "Pending Upload";
    } else if (designStatus === "processing") {
      return "Processing...";
    }

    return formatPostStatus(item.post_status || "-");
  }

  const formattedSchedule = openHomePost?.schedule_datetime
    ? dayjs(openHomePost?.schedule_datetime).format(
        "MMMM DD, YYYY [at] hh:mm A"
      )
    : "-";

  return (
    <div className="configure-post-container">
      <div className="post-type-tabs">
        {postTypes.map((type) => {
          const { value, label } = type;

          const isLoading = loadingPostType === value;
          const isActive = selectedPostType === value;

          const activity = designActivityMap[value];
          const hasDesignActivity = isDesignReady(activity);

          return (
            <div
              key={value}
              className={`post-type-tab ${isActive ? "active" : ""}`}
              onClick={() => handlePostTypeClick(value)}
            >
              <span className="post-type-icon">
                {isLoading ? (
                  <div className="red-circle-spinner" />
                ) : hasDesignActivity ? (
                  <div className="api-indicator">
                    <RoundTrueIcon />
                  </div>
                ) : null}
              </span>
              {label}
            </div>
          );
        })}
      </div>

      {step === "confirm" &&
        selectedPostType &&
        !designActivityMap[selectedPostType]?._id && (
          <div className="story-confirmation">
            <h4>
              Generate{" "}
              {postTypes.find((pt) => pt.value === selectedPostType)?.label} for
              Open Home
            </h4>
            <p>
              {postTypes.find((pt) => pt.value === selectedPostType)?.label} is not available by default for this open home. <br/> Please
              confirm if you'd like to create a {postTypes.find((pt) => pt.value === selectedPostType)?.label}.
            </p>
            <button onClick={handleUpdatePostType}>Confirm</button>
          </div>
        )}

      {step === "configure" && (
        <>
          <div className="post-info-header">
            <div className="info-item">
              <span className="label">Post Type</span>
              <span className="value">
                {postTypes.find((t) => t.value === selectedPostType)?.label ||
                  "N/A"}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Status</span>
              <span className="value">
                {getPostStatusDisplay(openHomePost)}
              </span>
            </div>
          </div>
          <div className="configure-post">
            <h3 className="section-title">Post Configuration</h3>

            <div className="right-actions">
              <ConfigureMenuIcon
                previewImageUrl={previewImages[postPreviewIndex]}
                onPreview={() => handlePreview(postPreviewIndex)}
              />
            </div>
          </div>
          <div
            className={`post-preview-wrapper ${isExpanded ? "expanded" : ""}`}
          >
            {isDesignReady(currentDesignActivity) ? (
              <div className="post-preview-card">
                <div className="post-header">
                  <img
                    src={
                      getImageUrl(
                        openHomePost?.agent_ref?.profile_image_ref
                          ?.compressed_file_path || ""
                      ) || "/images/profile-placeholder-image.png"
                    }
                    alt="Agent"
                    className="avatar"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src =
                        "/images/profile-placeholder-image.png";
                    }}
                  />
                  <div className="user-info">
                    <strong className="name">
                      {" "}
                      {openHomePost.agent_ref?.principal_agent_first_name}{" "}
                      {openHomePost.agent_ref?.principal_agent_last_name}
                    </strong>
                    <span className="date">{formattedSchedule}</span>
                  </div>
                </div>

                <div className="post-text">
                  <span className="post-content">
                    {isExpanded ? fullText : shortText}
                  </span>
                  <span className="see-more" onClick={toggleExpand}>
                    {isExpanded ? " See less" : " See more"}
                  </span>
                </div>

                <div className="carousel-container">
                  {filteredDesignActivities.length > 0 &&
                    renderDesignImage(
                      filteredDesignActivities[postPreviewIndex]
                    )}

                  {filteredDesignActivities.length > 1 && (
                    <>
                      <button
                        className="carousel-button left"
                        onClick={() =>
                          setPostPreviewIndex((prev) =>
                            prev === 0
                              ? filteredDesignActivities.length - 1
                              : prev - 1
                          )
                        }
                      >
                        <LeftArrowIcon />
                      </button>

                      <button
                        className="carousel-button right"
                        onClick={() =>
                          setPostPreviewIndex((prev) =>
                            prev === filteredDesignActivities.length - 1
                              ? 0
                              : prev + 1
                          )
                        }
                      >
                        <RightArrowIcon />
                      </button>
                    </>
                  )}
                </div>

                {filteredDesignActivities.length > 1 && (
                  <div className="dots-pagination">
                    {filteredDesignActivities.map((_, i) => (
                      <span
                        key={i}
                        className={`dot ${
                          i === postPreviewIndex ? "active" : ""
                        }`}
                        onClick={() => setPostPreviewIndex(i)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="post-preview-loading">
                <div className="spinner" />
                <h4>Your request is being processed.</h4>
                <p>
                  We’re working on your request! Creating a high-quality output
                  can take a little while. We appreciate your patience.
                </p>
                <p className="message">
                  Feel free to wait here, or browse other pages. We’ll send you
                  a notification when your request is completed.
                </p>
              </div>
            )}
          </div>
          <div className="text-config">
            <div className="post-text-header">
              <h4>Post Text</h4>
              <div className="toggle-same-text">
                <label className="switch-label">
                  Same text for all the platforms
                  <input
                    type="checkbox"
                    checked={sameTextToggle}
                    onChange={() => setSameTextToggle(!sameTextToggle)}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
            <div className="platform-header">
              <div className="platform-tabs">
                {tabs.map((tab) => (
                  <div
                    key={tab}
                    className={`platform-tab ${
                      activeTab === tab ? "active" : ""
                    }`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </div>
                ))}
              </div>
            </div>

            <div className="textarea">
              <textarea
                className="post-textarea"
                placeholder="Write your post content here..."
                defaultValue={""}
                onInput={(e: React.FormEvent<HTMLTextAreaElement>) => {
                  const target = e.currentTarget;
                  target.style.height = "auto";
                  target.style.height = `${target.scrollHeight}px`;
                }}
              />
            </div>
          </div>
        </>
      )}

      {isPreviewOpen && (
        <PreviewModal
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          images={previewImages}
          initialIndex={postPreviewIndex}
        />
      )}

      <Modal
        open={processingModalOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setModalOpen(false);
          }
        }}
        aria-labelledby="processing-modal"
        className="add-property-modal styled-modal"
        disableEscapeKeyDown
      >
        <div className="loading-content-wrapper">
          {isProcessing ? (
            <div className="loading-container">
              <div className="spinner" />
              <p className="loading-text">
                Processing your request
                <span className="desktop-break">
                  <br />
                </span>
                Please wait...
              </p>
            </div>
          ) : null}
        </div>
      </Modal>
    </div>
  );
};

export default OpenHomeConfigurePost;
